// API Service with proper payloads matching Swagger documentation
import apiClient from './apiClient';
import { API_ENDPOINTS } from './apiEndpoints';

/**
 * Authentication APIs
 */
export const authAPI = {
  // POST /Auth/login
  login: async (phoneNumber, password) => {
    const payload = {
      phoneNumber, // required
      password, // required, maxLength: 128
    };
    return apiClient.post(API_ENDPOINTS.AUTH.LOGIN, payload);
  },

  // GET /Auth/profile
  getProfile: async () => {
    return apiClient.get(API_ENDPOINTS.AUTH.PROFILE);
  },
};

/**
 * Users APIs
 */
export const usersAPI = {
  // POST /Users (Register)
  register: async (fullName, emailId, password, phoneNumber) => {
    const payload = {
      fullName, // required, minLength: 2, maxLength: 50
      emailId, // required, format: email
      password, // required
      phoneNumber, // required
    };
    return apiClient.post(API_ENDPOINTS.USERS.REGISTER, payload);
  },

  // GET /Users
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.USERS.GET_ALL);
  },

  // GET /Users/{id}
  getById: async id => {
    return apiClient.get(API_ENDPOINTS.USERS.GET_BY_ID(id));
  },

  // GET /Users/by-email/{email}
  getByEmail: async email => {
    return apiClient.get(API_ENDPOINTS.USERS.GET_BY_EMAIL(email));
  },

  // GET /Users/by-phone/{phone}
  getByPhone: async phone => {
    return apiClient.get(API_ENDPOINTS.USERS.GET_BY_PHONE(phone));
  },
};

/**
 * Products APIs
 */
export const productsAPI = {
  // POST /Products
  create: async productData => {
    const payload = {
      name: productData.name, // required
      sortDescription: productData.sortDescription, // required
      longDescription: productData.longDescription || null,
      price: productData.price, // required, number
      category: productData.category || null,
      keywords: productData.keywords || [],
      tags: productData.tags || [],
      whatInsideTheBox: productData.whatInsideTheBox || [],
      isActive: productData.isActive ?? true,
      isTrending: productData.isTrending ?? false,
      isFeatured: productData.isFeatured ?? false,
      visibility: productData.visibility || null,
      productType: productData.productType || null,
      media: productData.media || [], // Array of MediaTypeData
      quantity: productData.quantity || 0,
      digitalProductDetails: productData.digitalProductDetails || null,
      additionalDetails: productData.additionalDetails || {},
      features: productData.features || [],
      technologies: productData.technologies || [],
    };
    return apiClient.post(API_ENDPOINTS.PRODUCTS.CREATE, payload);
  },

  // GET /Products with filters. Supported by the server: SearchTerm, Category, ProductId,
  // IsTrending, IncludeHidden (admins only), CurrentPage (>= 1), ItemsPerPage (1-100),
  // SortBy (createddate | name | price | quantity | updateddate), SortOrder (asc | desc).
  // `config` is passed to axios (e.g. { signal } to cancel a stale request).
  getAll: async (filters = {}, config = {}) => {
    const params = {
      SearchTerm: filters.searchTerm || undefined,
      ProductId: filters.productId,
      IsTrending: filters.isTrending,
      IncludeHidden: filters.includeHidden,
      Category: filters.category || undefined,
      CurrentPage: filters.currentPage || 1,
      ItemsPerPage: filters.itemsPerPage || 10,
      SortBy: filters.sortBy,
      SortOrder: filters.sortOrder,
    };
    return apiClient.get(API_ENDPOINTS.PRODUCTS.GET_ALL, { ...config, params });
  },

  // GET /Products/{id}
  getById: async id => {
    return apiClient.get(API_ENDPOINTS.PRODUCTS.GET_BY_ID(id));
  },

  // PUT /Products/{id} - same body as create; a null/missing field is left unchanged.
  // Lists, media, additionalDetails and digitalProductDetails replace the stored value.
  update: async (id, productData) => {
    const payload = {
      name: productData.name ?? null,
      sortDescription: productData.sortDescription ?? null,
      longDescription: productData.longDescription ?? null,
      price: productData.price ?? null,
      category: productData.category ?? null,
      keywords: productData.keywords ?? null,
      tags: productData.tags ?? null,
      whatInsideTheBox: productData.whatInsideTheBox ?? null,
      features: productData.features ?? null,
      technologies: productData.technologies ?? null,
      isActive: productData.isActive ?? null,
      isTrending: productData.isTrending ?? null,
      isFeatured: productData.isFeatured ?? null,
      visibility: productData.visibility ?? null, // Public | Private | Hidden
      productType: productData.productType ?? null,
      media: productData.media ?? null, // [{ title, url, type: 'image', mimeType, fileSizeInBytes }]
      digitalProductDetails: productData.digitalProductDetails ?? null, // { edition, contentDescription, estimatedDownloadSizeMB }
      additionalDetails: productData.additionalDetails ?? null, // { key: 'string value' }
    };
    return apiClient.put(API_ENDPOINTS.PRODUCTS.UPDATE(id), payload);
  },

  // DELETE /Products/{id}
  delete: async id => {
    return apiClient.delete(API_ENDPOINTS.PRODUCTS.DELETE(id));
  },

  // GET /Products/trending
  getTrending: async () => {
    return apiClient.get(API_ENDPOINTS.PRODUCTS.GET_TRENDING);
  },
};

/**
 * Categories APIs
 */
export const categoriesAPI = {
  // POST /Category
  create: async categoryData => {
    const payload = {
      name: categoryData.name, // required, maxLength: 100
      description: categoryData.description || null, // maxLength: 500
      imageUrl: categoryData.imageUrl || null, // maxLength: 255
      parentCategoryId: categoryData.parentCategoryId || null,
      isActive: categoryData.isActive ?? true,
      displayOrder: categoryData.displayOrder || 0,
    };
    return apiClient.post(API_ENDPOINTS.CATEGORY.CREATE, payload);
  },

  // GET /Category with filters
  getAll: async (filters = {}) => {
    const params = {
      Name: filters.name,
      IsActive: filters.isActive,
      ParentCategoryId: filters.parentCategoryId,
      PageNumber: filters.pageNumber || 1,
      PageSize: filters.pageSize || 10,
    };
    return apiClient.get(API_ENDPOINTS.CATEGORY.GET_ALL, { params });
  },

  // GET /Category/{id}
  getById: async id => {
    return apiClient.get(API_ENDPOINTS.CATEGORY.GET_BY_ID(id));
  },

  // PUT /Category/{id}
  update: async (id, categoryData) => {
    const payload = {
      name: categoryData.name || null, // maxLength: 100
      description: categoryData.description || null, // maxLength: 500
      imageUrl: categoryData.imageUrl || null, // maxLength: 255
      parentCategoryId: categoryData.parentCategoryId || null,
      isActive: categoryData.isActive ?? null,
      displayOrder: categoryData.displayOrder ?? null,
    };
    return apiClient.put(API_ENDPOINTS.CATEGORY.UPDATE(id), payload);
  },

  // DELETE /Category/{id}
  delete: async id => {
    return apiClient.delete(API_ENDPOINTS.CATEGORY.DELETE(id));
  },

  // GET /Category/by-name/{name}
  getByName: async name => {
    return apiClient.get(API_ENDPOINTS.CATEGORY.GET_BY_NAME(name));
  },
};

/**
 * Orders APIs
 */
export const ordersAPI = {
  // POST /Orders/digital
  createDigitalOrder: async orderData => {
    const payload = {
      productId: orderData.productId, // required
      paymentMethod: orderData.paymentMethod || null,
      orderNotes: orderData.orderNotes || null,
      acceptedTerms: orderData.acceptedTerms === true, // required by the server
      buyerStateCode: orderData.buyerStateCode || null, // GST place of supply
      buyerGstin: orderData.buyerGstin || null,
      buyerBusinessName: orderData.buyerBusinessName || null,
    };
    return apiClient.post(API_ENDPOINTS.ORDERS.CREATE_DIGITAL, payload);
  },

  // GET /Orders with filters
  getAll: async (filters = {}) => {
    const params = {
      OrderId: filters.orderId,
      Status: filters.status,
      PaymentMethod: filters.paymentMethod,
      DateStart: filters.dateStart, // format: date-time
      DateEnd: filters.dateEnd, // format: date-time
      MinAmount: filters.minAmount, // number
      MaxAmount: filters.maxAmount, // number
      UserId: filters.userId,
      CurrentPage: filters.currentPage || 1,
      ItemsPerPage: filters.itemsPerPage || 10,
      SortBy: filters.sortBy,
      SortOrder: filters.sortOrder,
    };
    return apiClient.get(API_ENDPOINTS.ORDERS.GET_ALL, { params });
  },

  // GET /Orders/{id}
  getById: async id => {
    return apiClient.get(API_ENDPOINTS.ORDERS.GET_BY_ID(id));
  },

  // GET /Orders/order/{orderId}
  getByOrderId: async orderId => {
    return apiClient.get(API_ENDPOINTS.ORDERS.GET_BY_ORDER_ID(orderId));
  },

  // GET /Orders/my-orders
  getMyOrders: async (filters = {}) => {
    const params = {
      Status: filters.status,
      DateStart: filters.dateStart,
      DateEnd: filters.dateEnd,
      CurrentPage: filters.currentPage || 1,
      ItemsPerPage: filters.itemsPerPage || 10,
      SortBy: filters.sortBy,
      SortOrder: filters.sortOrder,
    };
    return apiClient.get(API_ENDPOINTS.ORDERS.GET_MY_ORDERS, { params });
  },

  // GET /Orders/payment-status/{orderId}
  getPaymentStatus: async orderId => {
    return apiClient.get(API_ENDPOINTS.ORDERS.GET_PAYMENT_STATUS(orderId));
  },
};

/**
 * Reviews APIs
 */
export const reviewsAPI = {
  // POST /Review
  create: async reviewData => {
    const payload = {
      productId: reviewData.productId, // required
      comment: reviewData.comment, // required
      rating: reviewData.rating, // number (1-5)
    };
    return apiClient.post(API_ENDPOINTS.REVIEWS.CREATE, payload);
  },

  // GET /Review
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.REVIEWS.GET_ALL);
  },

  // GET /Review/product/{productId}
  getByProduct: async productId => {
    return apiClient.get(API_ENDPOINTS.REVIEWS.GET_BY_PRODUCT(productId));
  },

  // GET /Review/user/{userId}
  getByUser: async userId => {
    return apiClient.get(API_ENDPOINTS.REVIEWS.GET_BY_USER(userId));
  },

  // PUT /Review/{reviewId}
  update: async (reviewId, reviewData) => {
    const payload = {
      productId: reviewData.productId, // required
      userId: reviewData.userId, // required
      userName: reviewData.userName, // required
      comment: reviewData.comment, // required
      rating: reviewData.rating, // number
      createdAt: reviewData.createdAt,
      updatedAt: new Date().toISOString(),
    };
    return apiClient.put(API_ENDPOINTS.REVIEWS.UPDATE(reviewId), payload);
  },

  // DELETE /Review/{reviewId}
  delete: async reviewId => {
    return apiClient.delete(API_ENDPOINTS.REVIEWS.DELETE(reviewId));
  },

  // GET /Review/product/{productId}/average-rating
  getAverageRating: async productId => {
    return apiClient.get(API_ENDPOINTS.REVIEWS.GET_AVERAGE_RATING(productId));
  },

  // GET /Review/check/{userId}/{productId}
  checkUserReview: async (userId, productId) => {
    return apiClient.get(
      API_ENDPOINTS.REVIEWS.CHECK_USER_REVIEW(userId, productId)
    );
  },
};

/**
 * GitHub Codes APIs
 */
export const githubAPI = {
  // POST /Github
  create: async githubData => {
    const payload = {
      title: githubData.title, // required
      description: githubData.description, // required
      tags: githubData.tags || [], // required, array
      author: githubData.author, // required
      createdAt: githubData.createdAt || new Date().toISOString(),
      link: githubData.link, // required
      videoLink: githubData.videoLink || '', // optional
      demoLink: githubData.demoLink || '', // optional
      slug: githubData.slug || null, // optional: blank means "make one from the title"
      requireSignIn: Boolean(githubData.requireSignIn),
    };
    return apiClient.post(API_ENDPOINTS.GITHUB.CREATE, payload);
  },

  // GET /Github/{key}: key is the entry's slug or id
  getByKey: async key => {
    return apiClient.get(API_ENDPOINTS.GITHUB.GET_BY_ID(encodeURIComponent(key)));
  },

  // GET /Github
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.GITHUB.GET_ALL);
  },
};

/**
 * Deliverables APIs
 */
export const deliverablesAPI = {
  // POST /api/Deliverable
  create: async deliverableData => {
    const payload = {
      productId: deliverableData.productId, // required
      edition: deliverableData.edition || null,
      deliverableContent: deliverableData.deliverableContent || [], // Array of DeliverableMediaData
      accessInstructions: deliverableData.accessInstructions || null,
      licenseTerms: deliverableData.licenseTerms || null,
      isActive: deliverableData.isActive ?? true,
    };
    return apiClient.post(API_ENDPOINTS.DELIVERABLES.CREATE, payload);
  },

  // GET /api/Deliverable/order/{orderId}
  getByOrder: async orderId => {
    return apiClient.get(API_ENDPOINTS.DELIVERABLES.GET_BY_ORDER(orderId));
  },

  // DELETE /api/Deliverable/product/{productId}
  deleteByProduct: async productId => {
    return apiClient.delete(
      API_ENDPOINTS.DELIVERABLES.DELETE_BY_PRODUCT(productId)
    );
  },

  // POST /api/Deliverable/track-access
  trackAccess: async accessData => {
    const payload = {
      orderId: accessData.orderId || null,
      productId: accessData.productId || null,
    };
    return apiClient.post(API_ENDPOINTS.DELIVERABLES.TRACK_ACCESS, payload);
  },
};

/**
 * Payment APIs
 */
export const paymentAPI = {
  // GET /api/payments/methods
  getMethods: async () => apiClient.get(API_ENDPOINTS.PAYMENTS.METHODS),

  // POST /api/checkout/{orderId}/pay - retry or switch payment method for an unpaid order
  startPayment: async (orderId, paymentMethod) =>
    apiClient.post(API_ENDPOINTS.CHECKOUT.PAY(orderId), { paymentMethod }),
};

/**
 * Health Check APIs
 */
export const healthAPI = {
  // GET /Health
  check: async () => {
    return apiClient.get(API_ENDPOINTS.HEALTH.CHECK);
  },

  // GET /Health/database
  checkDatabase: async () => {
    return apiClient.get(API_ENDPOINTS.HEALTH.DATABASE);
  },
};

// Export all APIs
export default {
  auth: authAPI,
  users: usersAPI,
  products: productsAPI,
  categories: categoriesAPI,
  orders: ordersAPI,
  reviews: reviewsAPI,
  github: githubAPI,
  deliverables: deliverablesAPI,
  payment: paymentAPI,
  health: healthAPI,
};
